import each from 'lodash/each'
import keys from 'lodash/keys'
import size from 'lodash/size'
import find from 'lodash/find'
import filter from 'lodash/filter'
import actionTypes from 'constants/actionTypes'
import { createId } from 'lib/id'
import * as selectNodes from 'selectors/graph/nodes'
import * as selectEdges from 'selectors/graph/edges'
import {
  createOneNode,
  deleteMultiNode,
  selectOneNode,
  deselectOneNode,
  pinMultiNode,
  unpinMultiNode,
  unpinOneNode
} from './nodes'
import {
  createOneEdge,
  deleteMultiEdge,
  selectOneEdge,
  deselectOneEdge,
  toggleDirectionality
} from './edges'
import {
  active,
  selectCreating,
  selectFocus,
  selectSelected,
  selectPinned,
  selectEdit,
  selectDrag,
  selectObjectDetails
} from 'selectors/graph'
import {
  selectCanEditFocus,
  selectCanEditActiveNodes,
  selectCanEditActiveEdges
} from 'selectors/permissions'
import type { RootState } from 'types/state'
import type { GraphEdgeMap, GraphNodeMap } from 'types/domain'
import type { ActionShape, DispatchFn, GetStateFn } from 'types/adapters'

type GraphAction = ActionShape
type Dispatch = DispatchFn<GraphAction>
type GetState = GetStateFn

type Position = {
  x: number
  y: number
}

type IdMap = Record<string, string | boolean>

const selectAllNodes = selectNodes.all as unknown as (state: RootState) => GraphNodeMap
const selectAllEdges = selectEdges.all as unknown as (state: RootState) => GraphEdgeMap
const selectAllNodesWithDeleted = selectNodes.all as unknown as (
  state: RootState,
  props: { params: { deleted: boolean } }
) => GraphNodeMap
const selectAllEdgesWithDeleted = selectEdges.all as unknown as (
  state: RootState,
  props: { params: { deleted: boolean } }
) => GraphEdgeMap
const selectNodeNeighborsWithDeleted = selectNodes.neighbors as unknown as (
  state: RootState,
  props: { params: { deleted: boolean; nodeId: string } }
) => string[]

const dispatchThunk = (dispatch: Dispatch, thunk: unknown) => dispatch(thunk as GraphAction)

export const ignoreDeleted = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const ns = selectAllNodes(state)
    const es = selectAllEdges(state)
    const f = selectFocus(state)
    const ps = selectPinned(state)
    const ss = selectSelected(state)

    const isDeleted = (id: string) => {
      if (ns[id]?.deleted) {
        return true
      }
      if (es[id]?.deleted) {
        return true
      }
      if (!(ns[id] || es[id])) {
        return true
      }
      return false
    }

    if (isDeleted(f.id)) {
      dispatch(unfocus())
    }

    each(ps.nodes, (_: string, id: string) => {
      if (isDeleted(id)) {
        dispatch(unpinOneNode({ id }))
      }
    })

    each(ss.nodes, (_: string, id: string) => {
      if (isDeleted(id)) {
        dispatch(deselectOneNode({ id }))
      }
    })

    each(ss.edges, (_: string, id: string) => {
      if (isDeleted(id)) {
        dispatch(deselectOneEdge({ id }))
      }
    })
  }
}

export const unfocus = () => {
  return {
    type: actionTypes.GRAPH.UNFOCUS
  }
}

export const clearModes = () => {
  return (dispatch: Dispatch) => {
    dispatchThunk(dispatch, stopEditing())
    dispatchThunk(dispatch, stopDragging())
    dispatchThunk(dispatch, stopCreatingEdge())
    dispatchThunk(dispatch, closeObjectDetails())
  }
}

export const focusOne = ({ id }: { id: string }) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    if (selectEdit(state).isEditing) {
      return
    }
    if (selectCreating(state).isCreating) {
      return
    }
    const f = selectFocus(state)
    if (f.id === id) {
      return
    }
    dispatchThunk(dispatch, clearModes())
    dispatch({
      type: actionTypes.GRAPH.FOCUS,
      payload: { id }
    })
  }
}

export const ensureFocus = (exclude: string[] = []) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const f = selectFocus(state)
    const ns = selectAllNodesWithDeleted(state, { params: { deleted: true } })
    const es = selectAllEdgesWithDeleted(state, { params: { deleted: true } })

    const getFocusable = (id: string, toTry: string[] = [], tried: Record<string, boolean> = {}): string | undefined => {
      if (ns[id] && !ns[id].deleted && !find(exclude, (x: string) => x === id)) {
        return id
      }
      if (es[id] && !es[id].deleted && !find(exclude, (x: string) => x === id)) {
        return id
      }

      tried[id] = true

      if (ns[id]) {
        const neighbors = selectNodeNeighborsWithDeleted(state, {
          params: {
            deleted: true,
            nodeId: id
          }
        })

        each(neighbors, (n: string) => {
          if (!tried[n]) {
            toTry.push(n)
          }
        })
      }

      if (es[id]) {
        const from = es[id].data.from
        const to = es[id].data.to
        if (!tried[from]) {
          toTry.push(from)
        }
        if (!tried[to]) {
          toTry.push(to)
        }
      }

      if (toTry.length > 0) {
        const next = toTry[0]
        if (next != null) {
          return getFocusable(next, toTry.slice(1), tried)
        }
      }

      for (const nId in ns) {
        const n = ns[nId]
        if (!n) {
          continue
        }
        if (!(n.deleted || find(exclude, (x: string) => x === nId))) {
          return nId
        }
      }

      for (const eId in es) {
        const e = es[eId]
        if (!e) {
          continue
        }
        if (!(e.deleted || find(exclude, (x: string) => x === eId))) {
          return eId
        }
      }
    }

    const id = getFocusable(f.id)
    if (id && id !== f.id) {
      dispatchThunk(dispatch, focusOne({ id }))
    }
  }
}

export const createAndFocusNode = ({ content, id }: { content?: string; id?: string } = {}) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const f = selectFocus(state)
    const es = selectAllEdges(state)
    dispatchThunk(dispatch, clearModes())

    if (f.isNode) {
      dispatch(pinMultiNode({ ids: [f.id] }))
    }

    const focusedEdge = es[f.id]
    if (f.isEdge && focusedEdge?.data?.from) {
      dispatch(pinMultiNode({ ids: [focusedEdge.data.from] }))
    }

    return Promise.resolve()
      .then(() => {
        const p = dispatch(createOneNode({
          ...(content !== undefined ? { content } : {}),
          ...(id !== undefined ? { id } : {})
        }) as unknown as GraphAction)
        return p
      })
      .then((action: unknown) => {
        const actionPayload = action as { payload?: { id?: string }; previousPayload?: { id?: string } }
        const actionId = actionPayload.payload?.id || actionPayload.previousPayload?.id
        if (!actionId) {
          return
        }
        dispatchThunk(dispatch, focusOne({ id: actionId }))
        return dispatchThunk(dispatch, openObjectDetails())
      })
  }
}

export const deselectAll = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const selected = selectSelected(state)
    if (size(selected.nodes) === 0 && size(selected.edges) === 0) {
      return
    }
    dispatch({ type: actionTypes.GRAPH.SELECTED.DESELECT_ALL })
  }
}

export const toggleSelectFocus = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const f = selectFocus(state)
    const s = selectSelected(state)
    const ns = selectNodes.all(state)
    const es = selectEdges.all(state)

    if (ns[f.id]) {
      if (s.nodes[f.id]) {
        return dispatch(deselectOneNode({ id: f.id }))
      }
      return dispatch(selectOneNode({ id: f.id }))
    }

    if (es[f.id]) {
      if (s.edges[f.id]) {
        return dispatch(deselectOneEdge({ id: f.id }))
      }
      return dispatch(selectOneEdge({ id: f.id }))
    }
  }
}

export const togglePinActive = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const ans = active.nodes(state)
    if (ans.length === 0) {
      return
    }
    const ps = selectPinned(state)
    const ids: string[] = []
    each(ans, (aId: string) => {
      each(ps.nodes, (_pn: string, pId: string) => {
        if (aId === pId) {
          ids.push(aId)
        }
      })
    })
    if (ids.length > 0) {
      return dispatch(unpinMultiNode({ ids }))
    }
    return dispatch(pinMultiNode({ ids: ans }))
  }
}

export const toggleDirectionalityActive = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const es = selectCanEditActiveEdges(state)
    if (!(es.length > 0)) {
      return
    }
    let p: Promise<unknown> = Promise.resolve()
    each(es, (e: string) => {
      p = p.then(() => dispatchThunk(dispatch, toggleDirectionality({ id: e })))
    })
    return p
  }
}

export const getNeighborhood = ({
  focus,
  pinned,
  selected,
  depth
}: {
  focus: string
  pinned: { nodes: IdMap }
  selected: { nodes: IdMap; edges: IdMap }
  depth: number
}) => {
  return (dispatch: Dispatch, getState: GetState) => {
    let ids: string[] = []

    if (size(pinned.nodes) > 0) {
      ids = ids.concat(keys(pinned.nodes))
    }
    if (size(selected.nodes) > 0) {
      ids = ids.concat(keys(selected.nodes))
    }
    if (size(selected.edges) > 0) {
      ids = ids.concat(keys(selected.edges))
    }

    const state = getState() as RootState
    const ns = selectAllNodesWithDeleted(state, { params: { deleted: true } })
    const es = selectAllEdgesWithDeleted(state, { params: { deleted: true } })

    let nextFocus: string | undefined = focus

    if (nextFocus && ns[nextFocus]?.local) {
      nextFocus = undefined
    }
    if (nextFocus && es[nextFocus]?.local) {
      nextFocus = undefined
    }

    ids = filter(ids, (id: string) => {
      if (ns[id]?.local) {
        return false
      }
      if (es[id]?.local) {
        return false
      }
      return true
    })

    if (!(ids.length || nextFocus)) {
      return
    }

    return dispatch({
      type: actionTypes.GRAPH.GET_NEIGHBORHOOD,
      payload: {
        focus: nextFocus,
        ids,
        depth
      },
      meta: {
        request: 'graph.getNeighborhood'
      }
    })
  }
}

export const createEdgesFromActive = ({ to }: { to: string }) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const ids = active.nodes(getState() as RootState)
    if (!ids.length) {
      return
    }

    let p: Promise<unknown> = Promise.resolve()
    let id: string | null = null

    each(ids.reverse(), (from: string) => {
      p = p
        .then(() => dispatchThunk(dispatch, createOneEdge({ from, to, content: '' })))
        .then((action: unknown) => {
          const payload = (action as { payload?: { id?: string; data?: { id?: string } } }).payload
          id = (id ?? payload?.id ?? payload?.data?.id) || null
        })
    })

    return p
      .then(() => {
        if (id) {
          return dispatchThunk(dispatch, focusOne({ id }))
        }
      })
      .then(() => {
        if (id) {
          return dispatchThunk(dispatch, openObjectDetails())
        }
      })
  }
}

export const createEdge = ({ from, to, content }: { from: string; to: string; content?: string }) => {
  return (dispatch: Dispatch, _getState: GetState) => {
    return dispatchThunk(dispatch, createOneEdge({
      from,
      to,
      ...(content !== undefined ? { content } : {})
    }))
  }
}

export const createNode = ({ content, id }: { content?: string; id?: string } = {}) => {
  return (dispatch: Dispatch, _getState: GetState) => {
    return dispatchThunk(dispatch, createOneNode({
      ...(content !== undefined ? { content } : {}),
      ...(id !== undefined ? { id } : {})
    }))
  }
}

export const createNodeFromActive = ({ position }: { position?: Position } = {}) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const ids = active.nodes(getState() as RootState)
    if (!ids.length) {
      return
    }
    return dispatchThunk(dispatch, createConnectedNode({
      ids,
      content: '',
      ...(position !== undefined ? { position } : {})
    }))
  }
}

export const createConnectedNode = ({
  ids,
  content,
  position
}: {
  ids: string[]
  content?: string
  position?: Position
}) => {
  return (dispatch: Dispatch) => {
    const id = createId()
    const to = id
    let p: Promise<unknown> = Promise.resolve().then(() => dispatchThunk(dispatch, createOneNode({
      id,
      ...(content !== undefined ? { content } : {}),
      ...(position !== undefined ? { position } : {})
    })))
    each(ids, (from: string) => {
      p = p.then(() => dispatchThunk(dispatch, createOneEdge({ from, to, content: '' })))
    })
    return p
      .then(() => dispatchThunk(dispatch, focusOne({ id })))
      .then(() => dispatchThunk(dispatch, openObjectDetails()))
  }
}

export const deleteActive = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const nIds = selectCanEditActiveNodes(state)
    const eIds = selectCanEditActiveEdges(state)
    if (!(nIds.length || eIds.length)) {
      return
    }

    const p = Promise.resolve()
      .then(() => dispatchThunk(dispatch, deleteMultiEdge({ ids: eIds })))
      .then(() => dispatchThunk(dispatch, deleteMultiNode({ ids: nIds })))

    return p
  }
}

export const startDragging = ({ id }: { id: string }) => {
  return (dispatch: Dispatch, _getState: GetState) => {
    dispatch({
      type: actionTypes.GRAPH.DRAG.START,
      payload: { id }
    })
  }
}

export const stopDragging = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const isDragging = selectDrag(state).isDragging
    if (!isDragging) {
      return
    }
    dispatch({ type: actionTypes.GRAPH.DRAG.STOP })
  }
}

export const startEditing = () => {
  return {
    type: actionTypes.GRAPH.EDIT.START
  }
}

export const stopEditing = (save = false) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { isEditing } = selectEdit(getState() as RootState)
    if (!isEditing) {
      return
    }
    dispatch({
      type: actionTypes.GRAPH.EDIT.STOP,
      payload: { save }
    })
  }
}

export const openObjectDetails = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const focus = selectFocus(state)
    const ns = selectAllNodes(state)
    const es = selectAllEdges(state)
    const o = ns[focus?.id] || es[focus?.id]
    if (!o) {
      return
    }
    dispatch({ type: actionTypes.GRAPH.OBJECT_DETAILS.OPEN })
    const canEdit = selectCanEditFocus(state)
    if (canEdit.length) {
      return dispatch(startEditing())
    }
  }
}

export const closeObjectDetails = (save = false) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { isOpen } = selectObjectDetails(getState() as RootState)
    if (!isOpen) {
      return
    }
    dispatch({ type: actionTypes.GRAPH.OBJECT_DETAILS.CLOSE })
    dispatchThunk(dispatch, stopEditing(save))
  }
}

export const startCreatingEdge = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const ids = active.nodes(state)
    const creating = selectCreating(state)
    if (ids.length === 0 || creating.isCreating) {
      return
    }
    dispatchThunk(dispatch, clearModes())
    dispatch({
      type: actionTypes.GRAPH.EDGE_CREATION.START,
      payload: { ids }
    })
  }
}

export const stopCreatingEdge = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState() as RootState
    const creating = selectCreating(state)
    if (!creating.isCreating) {
      return
    }
    dispatch({ type: actionTypes.GRAPH.EDGE_CREATION.STOP })
  }
}

export const setDepth = (depth: number) => {
  return {
    type: actionTypes.GRAPH.CULLING.SET_DEPTH,
    payload: { depth }
  }
}

export const resetDepth = () => {
  return {
    type: actionTypes.GRAPH.CULLING.RESET_DEPTH
  }
}

export const increaseDepth = () => {
  return {
    type: actionTypes.GRAPH.CULLING.INCREASE_DEPTH
  }
}

export const decreaseDepth = () => {
  return {
    type: actionTypes.GRAPH.CULLING.DECREASE_DEPTH
  }
}
